/**
 * Secure Deal Box - Main Component
 * Document management interface with upload, filters, and file list
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDealBox } from '@/hooks/useDealBox';
import { DEAL_BOX_TRANSLATIONS, DOCUMENT_TYPE_LABELS, CATEGORY_LABELS } from '@/constants/deal-box.constants';
import type { DealFile, DealBoxFilters, DocumentType, DocumentCategory } from '@/types/deal-box.types';
import { DealBoxUploadDialog } from './DealBoxUploadDialog';
import { DealBoxFileCard } from './DealBoxFileCard';
import { DealBoxShareDialog } from './DealBoxShareDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Upload, 
  Search, 
  Filter, 
  FolderOpen, 
  FileText,
  X,
  SortAsc,
  SortDesc
} from 'lucide-react';

interface SecureDealBoxProps {
  dealId: string;
  dealName?: string;
  compact?: boolean;
}

export function SecureDealBox({ dealId, dealName, compact = false }: SecureDealBoxProps) {
  const { language } = useLanguage();
  const t = DEAL_BOX_TRANSLATIONS[language as 'en' | 'fr'] || DEAL_BOX_TRANSLATIONS.en;
  const docTypeLabels = DOCUMENT_TYPE_LABELS[language as 'en' | 'fr'] || DOCUMENT_TYPE_LABELS.en;
  const categoryLabels = CATEGORY_LABELS[language as 'en' | 'fr'] || CATEGORY_LABELS.en;

  const { 
    loading, 
    fetchFiles, 
    deleteFile, 
    downloadFile,
    getPermissions 
  } = useDealBox(dealId);

  const [files, setFiles] = useState<DealFile[]>([]);
  const [filters, setFilters] = useState<DealBoxFilters>({});
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DealFile | null>(null);
  const [replaceFile, setReplaceFile] = useState<DealFile | null>(null);
  const [canUpload, setCanUpload] = useState(false);
  const [canShare, setCanShare] = useState(false);

  // Load files
  const loadFiles = useCallback(async () => {
    const result = await fetchFiles(filters);
    // Sort by date
    result.sort((a, b) => {
      const diff = b.uploadedAt.getTime() - a.uploadedAt.getTime();
      return sortOrder === 'desc' ? diff : -diff;
    });
    setFiles(result);
  }, [fetchFiles, filters, sortOrder]);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      const perms = await getPermissions();
      setCanUpload(perms.canUpload);
      setCanShare(perms.canShare);
    };
    checkPermissions();
  }, [getPermissions]);

  // Load files on mount and filter change
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Handle file delete
  const handleDelete = async (file: DealFile) => {
    if (!window.confirm(t.deleteConfirm)) return;
    
    const success = await deleteFile(file.id);
    if (success) {
      toast.success(t.deleteSuccess);
      loadFiles();
    } else {
      toast.error(t.deleteError);
    }
  };

  // Handle file download
  const handleDownload = (file: DealFile) => {
    downloadFile(file);
  };

  // Handle share
  const handleShare = (file: DealFile) => {
    setSelectedFile(file);
    setShareDialogOpen(true);
  };

  // Handle replace (version)
  const handleReplace = (file: DealFile) => {
    setReplaceFile(file);
    setUploadDialogOpen(true);
  };

  // Handle upload success
  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    setReplaceFile(null);
    loadFiles();
    toast.success(t.uploadSuccess);
  };

  // Handle share success
  const handleShareSuccess = () => {
    setShareDialogOpen(false);
    setSelectedFile(null);
    loadFiles();
    toast.success(t.shareSuccess);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({});
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== undefined && v !== '').length;

  // Document type options
  const documentTypeOptions: DocumentType[] = [
    'nda_signed', 'loi', 'ioi', 'pnl_annual', 'balance_sheet', 
    'tax_report', 'cim', 'evaluation', 'contract', 'license', 'other'
  ];

  // Category options
  const categoryOptions: DocumentCategory[] = [
    'financials', 'legal', 'cim', 'licenses', 'evaluations', 'misc'
  ];

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">{t.title}</CardTitle>
            </div>
            {canUpload && (
              <Button size="sm" onClick={() => setUploadDialogOpen(true)}>
                <Upload className="h-4 w-4 mr-1" />
                {t.uploadFile}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : files.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">{t.noDocuments}</p>
          ) : (
            <div className="space-y-2">
              {files.slice(0, 5).map(file => (
                <DealBoxFileCard
                  key={file.id}
                  file={file}
                  compact
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onShare={canShare ? handleShare : undefined}
                  onReplace={canUpload ? handleReplace : undefined}
                />
              ))}
              {files.length > 5 && (
                <p className="text-sm text-muted-foreground text-center">
                  +{files.length - 5} more documents
                </p>
              )}
            </div>
          )}
        </CardContent>

        <DealBoxUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          dealId={dealId}
          replaceFile={replaceFile}
          onSuccess={handleUploadSuccess}
        />

        {selectedFile && (
          <DealBoxShareDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            file={selectedFile}
            dealId={dealId}
            onSuccess={handleShareSuccess}
          />
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="h-6 w-6 text-primary" />
            {t.title}
          </h2>
          <p className="text-muted-foreground">{t.subtitle}</p>
          {dealName && (
            <Badge variant="outline" className="mt-2">{dealName}</Badge>
          )}
        </div>
        {canUpload && (
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            {t.uploadNew}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.searchPlaceholder}
                value={filters.searchQuery || ''}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                className="pl-9"
              />
            </div>

            {/* Document Type Filter */}
            <Select
              value={filters.documentType || 'all'}
              onValueChange={(value) => setFilters({ 
                ...filters, 
                documentType: value === 'all' ? undefined : value as DocumentType 
              })}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t.filterByType} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allTypes}</SelectItem>
                {documentTypeOptions.map(type => (
                  <SelectItem key={type} value={type}>
                    {docTypeLabels[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select
              value={filters.category || 'all'}
              onValueChange={(value) => setFilters({ 
                ...filters, 
                category: value === 'all' ? undefined : value as DocumentCategory 
              })}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t.filterByCategory} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allCategories}</SelectItem>
                {categoryOptions.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {categoryLabels[cat]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              title={sortOrder === 'desc' ? t.recentFirst : t.oldestFirst}
            >
              {sortOrder === 'desc' ? (
                <SortDesc className="h-4 w-4" />
              ) : (
                <SortAsc className="h-4 w-4" />
              )}
            </Button>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                {t.clearFilters}
              </Button>
            )}
          </div>

          {/* Active Filters Badge */}
          {activeFilterCount > 0 && (
            <div className="flex gap-2 mt-3">
              {filters.documentType && (
                <Badge variant="secondary">
                  {docTypeLabels[filters.documentType]}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, documentType: undefined })}
                  />
                </Badge>
              )}
              {filters.category && (
                <Badge variant="secondary">
                  {categoryLabels[filters.category]}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, category: undefined })}
                  />
                </Badge>
              )}
              {filters.searchQuery && (
                <Badge variant="secondary">
                  "{filters.searchQuery}"
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => setFilters({ ...filters, searchQuery: undefined })}
                  />
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* File List */}
      <div className="space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : files.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t.noDocuments}</p>
              {canUpload && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t.uploadNew}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          files.map(file => (
            <DealBoxFileCard
              key={file.id}
              file={file}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onShare={canShare ? handleShare : undefined}
              onReplace={canUpload ? handleReplace : undefined}
            />
          ))
        )}
      </div>

      {/* Stats */}
      {files.length > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          {files.length} document{files.length > 1 ? 's' : ''}
        </p>
      )}

      {/* Upload Dialog */}
      <DealBoxUploadDialog
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open) setReplaceFile(null);
        }}
        dealId={dealId}
        replaceFile={replaceFile}
        onSuccess={handleUploadSuccess}
      />

      {/* Share Dialog */}
      {selectedFile && (
        <DealBoxShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          file={selectedFile}
          dealId={dealId}
          onSuccess={handleShareSuccess}
        />
      )}
    </div>
  );
}
